// The failure branch for an optimistic store write.
//
// Store mutations are synchronous: they apply local state, dispatch the write,
// and return. `applyWrite` keeps that shape while giving the dispatched write
// somewhere to fail — it reverts, tells the user, and logs, all off the
// caller's stack. See `docs/runbooks/store-mutation.md`.
import type { PostgrestError } from '@supabase/supabase-js';

export type WriteFailureReporter = (message: string) => void;

let reportWriteFailure: WriteFailureReporter = () => {};

/**
 * Configure the application-owned presentation for refused writes.
 *
 * The write coordinator is shared infrastructure and must not know which UI
 * primitive presents failures. Application composition installs that adapter
 * once at startup; tests can install a recorder without importing UI code.
 */
export function setWriteFailureReporter(reporter: WriteFailureReporter): void {
  reportWriteFailure = reporter;
}

/**
 * A refusal that RLS expresses by filtering rather than erroring.
 *
 * A denied UPDATE or DELETE is not an error at the protocol level: PostgREST
 * matches no rows and returns 204 with an empty body, which is byte-identical
 * to a successful write. Asking for the affected rows back is what makes the
 * two distinguishable, so every write routed through here must end in
 * `.select(...)` — hence `data` being required rather than optional.
 */
export interface WriteResult {
  data: unknown[] | null;
  error: PostgrestError | null;
}

export type WriteFailure =
  /** The database refused the write, by error or by filtering every row. */
  | { kind: 'denied'; error: PostgrestError | null }
  /** The request never completed — offline, timeout, aborted. */
  | { kind: 'unreachable'; error: unknown };

/** Postgres raises this when a policy or grant refuses the statement outright. */
const INSUFFICIENT_PRIVILEGE = '42501';

function describe(failure: WriteFailure, opts: ApplyWriteOptions): string {
  const { noun, operation = 'update' } = opts;
  if (failure.kind === 'unreachable') return `Couldn't reach the server to save ${noun}.`;

  // An explicit 42501 is a denial whatever the statement was.
  if (failure.error?.code === INSUFFICIENT_PRIVILEGE) return `You don't have access to change ${noun}.`;

  // Zero rows with no error is RLS filtering. For an update that is the only
  // reading available — the row was on screen a moment ago, so it exists. For a
  // delete it is genuinely ambiguous: someone else may have removed the row
  // first, and asserting a permission problem would then be a lie. Say only
  // what is true in both cases and let the refetch settle the state.
  if (!failure.error) {
    return operation === 'delete' ? `Couldn't delete ${noun}.` : `You don't have access to change ${noun}.`;
  }

  return `Couldn't save ${noun}.`;
}

export interface ApplyWriteOptions {
  /**
   * What was being written, as a fragment that reads inside "Couldn't save
   * ___" — `'this rock'`, `'the scorecard'`. Lowercase, no trailing period.
   */
  noun: string;
  /** Tag for the console line, conventionally the mutation name. */
  context: string;
  /**
   * Shapes the message when the database returns no rows and no error. Defaults
   * to `'update'`; pass `'delete'` so a row someone else already removed is not
   * reported as a permission problem.
   */
  operation?: 'update' | 'delete';
  /**
   * How many rows the write is meant to affect. `'one'` (the default) targets a
   * specific row by id, so zero rows back means it was refused. `'set'` targets
   * whatever matches a non-id filter — an empty match is an ordinary outcome
   * there, so only an explicit error counts as a failure.
   */
  targets?: 'one' | 'set';
  /**
   * Undo the optimistic change. Omit it when the inverse is awkward — cascades,
   * entries arrays — and pass `refetch` instead, which ends consistent with the
   * database at the cost of a round trip.
   */
  rollback?: () => void;
  /** Re-read the affected slice. Used when `rollback` is absent. */
  refetch?: () => void;
  /**
   * Run only once the write is known to have landed. Audit entries and
   * notifications belong here: firing them beside the dispatch records history
   * for a write the database may still refuse.
   *
   * Receives the rows the write returned, so a caller whose write computes its
   * own result — an RPC that returns the state it just wrote — can reconcile
   * optimistic state against the database rather than against its own guess.
   */
  onSuccess?: (data: unknown[]) => void;
}

/**
 * Dispatch an already-built Supabase write and handle its failure.
 *
 * The write must request its affected rows (`.select('id')` is enough) or a
 * denial cannot be told from a success — see `WriteResult`.
 *
 * Fire-and-forget by design: the caller has already applied optimistic state
 * and does not await this. Nothing here throws.
 */
export function applyWrite(write: PromiseLike<WriteResult>, opts: ApplyWriteOptions): Promise<boolean> {
  return Promise.resolve(write).then(
    ({ data, error }) => {
      const affectedEnough = opts.targets === 'set' ? data != null : !!data && data.length > 0;
      if (!error && affectedEnough) {
        try {
          opts.onSuccess?.(data ?? []);
        } catch (successError) {
          console.error(`[${opts.context}] post-write step failed:`, successError);
        }
        return true;
      }
      handle({ kind: 'denied', error }, opts);
      return false;
    },
    (error) => {
      handle({ kind: 'unreachable', error }, opts);
      return false;
    },
  );
}

function handle(failure: WriteFailure, opts: ApplyWriteOptions): void {
  // Revert first: the message is about state the user can already see, and it
  // should not describe a change that is still on screen. Guarded, because a
  // recovery that throws must not also cost the user the explanation — and an
  // escaped rejection here would surface as an unhandled one, far from its
  // cause.
  try {
    if (opts.rollback) opts.rollback();
    else opts.refetch?.();
  } catch (recoveryError) {
    console.error(`[${opts.context}] recovery failed:`, recoveryError);
  }

  const detail = failure.kind === 'unreachable' ? failure.error : failure.error?.message;
  console.error(`[${opts.context}] write refused:`, detail ?? '0 rows affected');
  reportWriteFailure(describe(failure, opts));
}
